<div class="w-full h-full flex flex-col p-4 dark:text-app-gray-300">
  <div class="flex items-start justify-between mb-1 gap-4">
    <div class="flex items-center gap-3">
      <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Commerce Events</h2>
      <span class="text-[11px] text-gray-500 dark:text-gray-400">Records auto-expire after 365 days.</span>
    </div>
    <button type="button"
            class="text-[11px] uppercase tracking-wide border border-gray-300 dark:border-[#434863] rounded px-2 py-1
                   text-gray-700 dark:text-gray-300 hover:text-app-teal-100 hover:border-app-teal-100 transition"
            (click)="loadList()">
      <i class="fa-solid fa-rotate mr-1"></i>Refresh
    </button>
  </div>
  <p class="text-xs text-gray-500 dark:text-gray-400 mb-4">
    One row per inbound commerce webhook (post-HMAC) that reached the dispatcher. Captures the action F2 took so support and audit can answer "what did F2 do?". Paste an event ID to load full diagnostics.
  </p>

  <!-- ============================================================ LOOKUP ============================================================ -->
  <div class="border border-gray-200 dark:border-[#434863] rounded p-3 mb-4 bg-gray-50 dark:bg-app-black-100/40">
    <label class="block text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
      Lookup by event ID
    </label>
    <div class="flex gap-2">
      <input type="text" [(ngModel)]="lookupId" (keyup.enter)="onLookup()"
             placeholder="Paste an ObjectId (24 hex chars) — or the whole 'Event ID: …' line"
             class="flex-1 bg-white border border-gray-300 text-gray-900 rounded px-3 py-1.5 text-sm font-mono dark:bg-app-black-100 dark:border-[#434863] dark:text-white" />
      <button type="button" class="dark-btn"
              [class.dark-btn-disabled]="loadingLookup"
              [disabled]="loadingLookup"
              (click)="onLookup()">
        <i class="fa-solid fa-magnifying-glass mr-1"></i>
        <ng-container *ngIf="!loadingLookup">Lookup</ng-container>
        <ng-container *ngIf="loadingLookup">Loading…</ng-container>
      </button>
      <button *ngIf="selected || lookupId" type="button" class="dark-btn"
              (click)="clearSelection()">
        <i class="fa-solid fa-xmark mr-1"></i>Clear
      </button>
    </div>
    <p *ngIf="lookupErr" class="mt-2 text-xs text-red-400">{{ lookupErr }}</p>
  </div>

  <!-- ============================================================ DETAIL ============================================================ -->
  <div *ngIf="selected" class="border border-app-teal-100 dark:border-app-teal-100/60 rounded mb-6 bg-white dark:bg-app-black-500">
    <div class="flex items-center justify-between px-3 py-2 bg-app-teal-100/10 border-b border-app-teal-100/30">
      <div class="flex items-center gap-3">
        <code class="text-sm font-mono font-semibold text-app-teal-100">{{ selected._id }}</code>
        <button type="button" class="text-gray-500 dark:text-gray-400 hover:text-app-teal-100"
                (click)="copyText(selected._id)" title="Copy event ID">
          <i class="fa-regular fa-copy"></i>
        </button>
        <span class="text-xs text-gray-500 dark:text-gray-400">{{ formatRelative(selected.created_at) }}</span>
        <span class="text-[11px] uppercase tracking-wide px-2 py-0.5 rounded border"
              [ngClass]="{
                'bg-emerald-900/30 border-emerald-500/40 text-emerald-300': selected.result === 'success',
                'bg-red-900/30 border-red-500/40 text-red-300': selected.result === 'failed',
                'bg-amber-900/30 border-amber-500/40 text-amber-200': selected.result === 'partial',
                'bg-gray-700 border-gray-500 text-gray-300': selected.result === 'noop',
                'bg-teal-900/30 border-teal-500/40 text-teal-200': selected.result === 'already_activated',
                'bg-slate-800 border-slate-500/40 text-slate-300': selected.result === 'already_deactivated',
                'bg-violet-900/30 border-violet-500/40 text-violet-200': selected.result === 'test'
              }">{{ selected.result }}</span>
        <span *ngIf="selected.details?.['new_user']"
              class="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded border bg-indigo-900/30 border-indigo-500/40 text-indigo-300"
              title="A new Cognito user was created for this activation">
          <i class="fa-solid fa-user-plus mr-1"></i>New User
        </span>
        <span *ngIf="selected.details?.['was_never_provisioned']"
              class="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded border bg-amber-900/30 border-amber-500/40 text-amber-300"
              title="User isn't in Cognito at all — likely fallout of an earlier failed activate (auth bug, no api_key, missing scanner mapping). The revoke landed cleanly but investigate the original sale.">
          <i class="fa-solid fa-triangle-exclamation mr-1"></i>Never Provisioned
        </span>
      </div>
      <div class="flex items-center gap-3">
        <button type="button" class="text-xs text-gray-500 dark:text-gray-400 hover:text-app-teal-100"
                (click)="copyText(prettyJson(selected))" title="Copy the full event as JSON">
          <i class="fa-regular fa-copy mr-1"></i>copy JSON
        </button>
        <button type="button" class="text-xs text-gray-500 dark:text-gray-400 hover:text-app-teal-100"
                (click)="copyLink(selected._id)"
                [title]="'Copy deep-link to this event: ' + linkFor(selected._id)">
          <i class="fa-solid fa-link mr-1"></i>copy link
        </button>
      </div>
    </div>
    <div class="p-3 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-xs">
      <div><span class="font-semibold text-gray-700 dark:text-gray-300">Customer:</span> <code class="text-app-teal-100">{{ selected.customer || '—' }}</code></div>
      <div><span class="font-semibold text-gray-700 dark:text-gray-300">Integration:</span> <code class="text-app-teal-100">{{ selected.integration || '—' }}</code></div>
      <div><span class="font-semibold text-gray-700 dark:text-gray-300">Topic:</span> <code class="text-app-teal-100">{{ selected.topic || '—' }}</code></div>
      <div><span class="font-semibold text-gray-700 dark:text-gray-300">Status:</span> <code class="text-app-teal-100">{{ selected.status || '—' }}</code></div>
      <div><span class="font-semibold text-gray-700 dark:text-gray-300">Action:</span> <code class="text-app-teal-100">{{ selected.action }}</code></div>
      <div><span class="font-semibold text-gray-700 dark:text-gray-300">Result:</span> <code class="text-app-teal-100">{{ selected.result }}</code></div>
      <div class="md:col-span-2 break-all">
        <span class="font-semibold text-gray-700 dark:text-gray-300">Email:</span>
        <code class="text-app-teal-100">{{ selected.email || '—' }}</code>
        <ng-container *ngIf="selected.first_name || selected.last_name">
          <span class="ml-2 text-gray-400">({{ selected.first_name }} {{ selected.last_name }})</span>
        </ng-container>
      </div>
      <div class="md:col-span-2">
        <span class="font-semibold text-gray-700 dark:text-gray-300">Scanners:</span>
        <code class="text-app-teal-100">{{ (selected.scanners || []).join(', ') || '—' }}</code>
      </div>
      <div *ngIf="selected.action_summary" class="md:col-span-2">
        <span class="font-semibold text-gray-700 dark:text-gray-300">What we did:</span>
        <span class="text-app-teal-100">{{ selected.action_summary }}</span>
      </div>
      <div *ngIf="selected.endpoint" class="md:col-span-2 break-all">
        <span class="font-semibold text-gray-700 dark:text-gray-300">Endpoint:</span>
        <code class="text-app-teal-100">{{ selected.endpoint }}</code>
      </div>
      <div *ngIf="selected.source" class="md:col-span-2">
        <span class="font-semibold text-gray-700 dark:text-gray-300">Source:</span>
        <code class="text-app-teal-100">{{ selected.source }}</code>
      </div>
      <div *ngIf="selected.error" class="md:col-span-2">
        <span class="font-semibold text-gray-700 dark:text-gray-300">Error:</span>
        <code class="text-red-400">{{ selected.error }}</code>
      </div>
      <div *ngIf="selected.line_items && selected.line_items.length" class="md:col-span-2">
        <div class="font-semibold text-gray-700 dark:text-gray-300 mb-1">Line items</div>
        <pre class="bg-gray-50 dark:bg-app-black-100 border border-gray-300 dark:border-[#434863] rounded p-2 font-mono leading-snug text-gray-800 dark:text-gray-200 overflow-x-auto max-h-64">{{ prettyJson(selected.line_items) }}</pre>
      </div>
      <div *ngIf="selected.details" class="md:col-span-2">
        <div class="font-semibold text-gray-700 dark:text-gray-300 mb-1">Details</div>
        <pre class="bg-gray-50 dark:bg-app-black-100 border border-gray-300 dark:border-[#434863] rounded p-2 font-mono leading-snug text-gray-800 dark:text-gray-200 overflow-x-auto max-h-64">{{ prettyJson(selected.details) }}</pre>
      </div>
    </div>
  </div>

  <!-- ============================================================ LIST ============================================================ -->
  <div class="border border-gray-200 dark:border-[#434863] rounded flex flex-col flex-1 min-h-0">
    <div class="px-3 py-2 bg-gray-100 dark:bg-app-black-100 border-b border-gray-200 dark:border-[#434863] flex items-center gap-3 flex-wrap flex-none">
      <span class="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Recent events</span>
      <!-- Server-side count (IT-F2-134). Populated from the first-block
           fetch's `total` field; updates when filter/sort change. -->
      <span *ngIf="totalCount !== null" class="text-[11px] text-gray-500 dark:text-gray-400 font-mono">
        {{ totalCount | number }} events
      </span>
      <div class="ml-auto flex items-center gap-2">
        <button type="button"
                class="text-[10px] uppercase tracking-wide border rounded px-2 py-0.5 transition inline-flex items-center gap-1.5"
                [class.text-app-teal-100]="liveOn && !liveSuspended"
                [class.border-app-teal-100]="liveOn && !liveSuspended"
                [class.text-gray-500]="!(liveOn && !liveSuspended)"
                [class.dark:text-gray-400]="!(liveOn && !liveSuspended)"
                [class.border-gray-300]="!(liveOn && !liveSuspended)"
                [class.dark:border-[#434863]]="!(liveOn && !liveSuspended)"
                (click)="toggleLive()"
                [title]="liveOn ? 'Live updates are ON — new events stream in at the top' : 'Turn on live updates — new events will stream in at the top'">
          <span class="live-dot" [class.live-dot--on]="liveOn && !liveSuspended"></span>
          Live
        </button>
        <button type="button"
                class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 hover:text-app-teal-100 border border-gray-300 dark:border-[#434863] rounded px-2 py-0.5"
                (click)="loadList()" title="Refresh — re-fetch the current filter/sort from block 0">
          <i class="fa-solid fa-rotate mr-1"></i>Refresh
        </button>
        <button type="button"
                class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 hover:text-app-teal-100 border border-gray-300 dark:border-[#434863] rounded px-2 py-0.5"
                (click)="onCopyIds()"
                [title]="selectedCount > 0 ? 'Copy IDs of the ' + selectedCount + ' selected event(s)' : 'Copy IDs of every visible event'">
          <i class="fa-regular fa-copy mr-1"></i>{{ copyButtonLabel('Copy IDs') }}
        </button>
        <button type="button"
                class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 hover:text-app-teal-100 border border-gray-300 dark:border-[#434863] rounded px-2 py-0.5"
                (click)="onCopyJson()"
                [title]="selectedCount > 0 ? 'Copy the ' + selectedCount + ' selected event(s) as JSON' : 'Copy every visible event as JSON'">
          <i class="fa-regular fa-copy mr-1"></i>{{ copyButtonLabel('Copy JSON') }}
        </button>
        <button type="button"
                class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 hover:text-app-teal-100 border border-gray-300 dark:border-[#434863] rounded px-2 py-0.5"
                (click)="onCopyCsv()"
                [title]="selectedCount > 0 ? 'Copy the ' + selectedCount + ' selected event(s) as CSV' : 'Copy every visible event as CSV'">
          <i class="fa-regular fa-copy mr-1"></i>{{ copyButtonLabel('Copy CSV') }}
        </button>
        <!-- IT-F2-338: split-button server-side full-dataset export. Main click
             = CSV default; chevron opens dropdown to pick CSV or Excel (.xls).
             Both respect the grid's current filter/sort (b64 JSON to /export). -->
        <div class="relative inline-flex" (mouseleave)="exportMenuOpen = false">
          <button type="button"
                  class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 hover:text-app-teal-100 border border-gray-300 dark:border-[#434863] rounded-l px-2 py-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                  [disabled]="exporting"
                  (click)="onExportCsv('csv')"
                  title="Download every row matching the current filter/sort as CSV (not just the visible page)">
            <i class="fa-solid mr-1"
               [class.fa-spin]="exporting"
               [class.fa-circle-notch]="exporting"
               [class.fa-file-arrow-down]="!exporting"></i>{{ exporting ? 'Exporting…' : 'Export' }}
          </button>
          <button type="button"
                  class="text-[10px] text-gray-500 dark:text-gray-400 hover:text-app-teal-100 border border-l-0 border-gray-300 dark:border-[#434863] rounded-r px-1.5 py-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                  [disabled]="exporting"
                  (click)="exportMenuOpen = !exportMenuOpen"
                  title="Pick export format">
            <i class="fa-solid fa-caret-down"></i>
          </button>
          <div *ngIf="exportMenuOpen"
               class="absolute right-0 top-full mt-1 z-10 min-w-[140px] bg-white dark:bg-app-black-500 border border-gray-300 dark:border-[#434863] rounded shadow-lg">
            <button type="button"
                    class="w-full text-left text-[11px] px-3 py-1.5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-app-black-100 hover:text-app-teal-100"
                    (click)="onExportCsv('csv')">
              <i class="fa-solid fa-file-csv mr-2"></i>CSV (.csv)
            </button>
            <button type="button"
                    class="w-full text-left text-[11px] px-3 py-1.5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-app-black-100 hover:text-app-teal-100"
                    (click)="onExportCsv('xls')">
              <i class="fa-solid fa-file-excel mr-2"></i>Excel (.xls)
            </button>
          </div>
        </div>
      </div>
    </div>

    <p *ngIf="listErr" class="p-3 text-xs text-red-400">{{ listErr }}</p>

    <!-- Infinite row model manages rows via its datasource — DO NOT bind
         [rowData], and DO NOT bind [statusBar] (see init_grid + the fleet
         gotcha in commerce-events.component.ts). Grid is always mounted
         so the datasource can be wired on init even before first data
         arrives. -->
    <ag-grid-angular class="flex-1 w-full h-[600px]"
                     [theme]="theme"
                     [gridOptions]="grid.options"
                     [columnDefs]="grid.columnDefs">
    </ag-grid-angular>
  </div>
</div>
